import api from './api';

// Helper function to check authentication
const checkAuth = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required. Please sign in.');
  }
  return token;
};

// Get portfolio trend data (날짜별 포트폴리오 가치 추이)
export const getPortfolioTrend = async (days = 30) => {
  try {
    checkAuth();
    
    const response = await api.get('/api/data_predator/portfolio-trend/', {
      params: { days }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch portfolio trend', error);
    throw error;
  }
};

export const getDashboardStats = async () => {
  try {
    checkAuth(); // Check authentication before making API call
    
    const response = await api.get('/api/data_predator/dashboard-stats/');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch dashboard stats', error);
    throw error;
  }
};

export const getMonthlyDividendStats = async () => {
  try {
    checkAuth(); // Check authentication before making API call
    
    const response = await api.get('/api/data_predator/monthly-dividend-stats/');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch monthly dividend stats', error);
    throw error;
  }
};

// Helper function to get current stock price using existing stock service
export const getCurrentStockPrice = async (stockSymbol) => {
  try {
    checkAuth();
    
    // Get stock data for the last 5 days to get the most recent price
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    const response = await api.get('/api/data_predator/get-stock-info/', {
      params: {
        stock_code: stockSymbol,
        date_from: startDate,
        date_end: endDate,
      },
      timeout: 5000, // 5초 타임아웃으로 단축
    });

    const data = response.data.data;
    if (data && data.length > 0) {
      // Return the most recent closing price
      const mostRecent = data[data.length - 1];
      return {
        price: mostRecent.Close,
        date: mostRecent.Date
      };
    }
    
    return { price: 0, date: null };
  } catch (error) {
    console.error(`Failed to fetch current price for ${stockSymbol}`, error);
    // 타임아웃이나 에러 발생시 기본값 반환
    return { price: 0, date: null };
  }
};

// Helper function to format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format percentage
export const formatPercentage = (rate) => {
  const sign = rate >= 0 ? '+' : '';
  return `${sign}${rate.toFixed(2)}%`;
};

// Helper function to calculate profit/loss rate
export const calculateProfitLossRate = (currentPrice, avgBuyPrice) => {
  if (avgBuyPrice === 0) return 0;
  return ((currentPrice - avgBuyPrice) / avgBuyPrice) * 100;
};

// ===== NEW DAILY HOLDING API SERVICES =====

// Get daily holdings summary
export const getDailyHoldingsSummary = async (date = null) => {
  try {
    checkAuth();
    
    const params = {};
    if (date) {
      params.date = date;
    }
    
    const response = await api.get('/api/data_predator/daily-holdings/summary/', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch daily holdings summary', error);
    throw error;
  }
};

// Get daily holdings by account
export const getDailyHoldingsByAccount = async (accountId = null, date = null, latestOnly = true) => {
  try {
    checkAuth();
    
    const params = {};
    if (accountId) {
      params.account_id = accountId;
    }
    if (date) {
      params.date = date;
    }
    // 기본적으로 각 계좌별 최신 데이터만 조회
    if (latestOnly && !date) {
      params.latest_only = 'true';
    }
    
    const response = await api.get('/api/data_predator/daily-holdings-api/', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch daily holdings by account', error);
    throw error;
  }
};

// Get daily holdings by stock
export const getDailyHoldingsByStock = async (stockId = null, date = null) => {
  try {
    checkAuth();
    
    const params = {};
    if (stockId) {
      params.stock_id = stockId;
    }
    if (date) {
      params.date = date;
    }
    
    const response = await api.get('/api/data_predator/daily-holdings-api/', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch daily holdings by stock', error);
    throw error;
  }
};

// Get daily holdings trend (historical data)
export const getDailyHoldingsTrend = async (days = 30) => {
  try {
    checkAuth();
    
    const response = await api.get('/api/data_predator/daily-holdings/trend/', {
      params: { days }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch daily holdings trend', error);
    throw error;
  }
};

// Get account list for dropdown
export const getAccountList = async () => {
  try {
    checkAuth();
    
    const response = await api.get('/api/data_predator/accounts/');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch account list', error);
    throw error;
  }
};

// Enhanced dashboard stats with daily holdings
export const getEnhancedDashboardStats = async () => {
  try {
    checkAuth();
    
    // Fetch multiple data sources in parallel
    const [dashboardStats, dailyHoldingsSummary, accountHoldings] = await Promise.all([
      getDashboardStats(),
      getDailyHoldingsSummary(),
      getDailyHoldingsByAccount()
    ]);

    return {
      ...dashboardStats,
      daily_holdings_summary: dailyHoldingsSummary,
      account_holdings: accountHoldings,
    };
  } catch (error) {
    console.error('Failed to fetch enhanced dashboard stats', error);
    throw error;
  }
};

// Refresh dashboard data (Backend DB 업데이트 + Frontend 데이터 재조회)
export const refreshDashboardData = async () => {
  try {
    checkAuth();
    
    // console.log('Dashboard data refresh started...');
    
    // 1. Backend에서 실제 DB 데이터 업데이트 (일일 보유 현황 스냅샷 생성)
    const refreshResponse = await api.post('/api/data_predator/refresh-data/', {
      date: new Date().toISOString().split('T')[0], // 오늘 날짜
      force: true // 기존 데이터가 있어도 강제 업데이트
    });
    
    // console.log('Backend data refresh completed:', refreshResponse.data);
    
    // 2. Frontend에서 최신 데이터 재조회
    const [dashboardStats, monthlyDividendStats, dailyHoldingsData] = await Promise.all([
      getDashboardStats(),
      getMonthlyDividendStats(),
      getDailyHoldingsByAccount()
    ]);

    // console.log('Frontend data refresh completed successfully');
    
    return {
      backendRefresh: refreshResponse.data,
      dashboardStats,
      monthlyDividendStats,
      dailyHoldingsData,
      refreshedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('Failed to refresh dashboard data', error);
    throw error;
  }
};
